import React from "react";
import { Plus, ChevronDown, FileSpreadsheet, Check, X, Trash2, Users, ChevronRight, Download, Loader2 } from "lucide-react";
import { clsx } from "clsx";
import { usePlanAssignPage } from "@hooks";
import { ExcelUploadModal, AssignmentRegistModal, PageHeader, FilterBar, DataTable } from "@components";

export function CompanyAssignment() {
  const {
    filters, setFilters, query, page,
    editingRows, startEdit, cancelEdit, updateEditRow, saveRow, getKey,
    onSearch,
    isExcelOpen, setIsExcelOpen, handleExcelUpload, isUploading,
    selectedIds, toggleSelectRow, anyChecked,
    handleAddRound,
    isRegistModalOpen, setIsRegistModalOpen, handleRegistClick, registAssignment, isRegisting,
    handleDeleteClick, isDeleting, staffList, auth, bizList, expandedRows, toggleExpand, downloadExcel, isDownloadingExcel,
  } = usePlanAssignPage();

  const handleReset = () => {
    setFilters({
      branch: '', companyName: '', jurisdiction: '', assignee: '', worksiteName: '', bizRegNo: '', pageNum: 1
    });
  };

  const initialHelpText = `각 사업장을 방문하여 컨설팅을 진행할 수행요원(담당자)을 배정하고 관리하는 페이지입니다.

[주요 기능 안내]
1. 담당자 배정 (인라인 편집): 표 우측의 '방문 담당자' 칸을 클릭하면 즉시 다른 담당자로 변경할 수 있습니다. 변경 후 [V] 체크 버튼을 누르면 저장됩니다.
2. 신규 배정 등록: [등록] 버튼을 눌러 특정 사업장에 담당자를 신규로 수동 배정합니다.
3. 엑셀 일괄 업로드: 대량의 사업장과 담당자를 한 번에 매칭하여 업로드할 때 사용합니다.
4. 삭제: 하위 목록의 체크박스를 선택하고 [삭제] 버튼을 누르면 배정 내역이 삭제됩니다.`;

  // 🌟 (수정) 그룹(통합) 행에서 "선택", "회차" 컬럼 완전 제거 (총 10개 컬럼)
  const tableColumns = [
    { label: "", width: "w-12" }, // 1. 펼치기 아이콘
    { label: "회사명", className: "min-w-[160px]" }, // 2
    { label: "대표자" }, // 3
    { label: "근로자 수", align: "text-right" }, // 4
    { label: "업종" }, // 5
    { label: "주소", className: "min-w-[200px]" }, // 6
    { label: "업체 담당자" }, // 7
    { label: "담당자 연락처" }, // 8
    { label: "방문 담당자 1 (통합)", className: "min-w-[180px] bg-blue-50 border-l border-blue-100 text-blue-700 font-bold" }, // 9
    { label: "방문 담당자 2 (회차별)", className: "min-w-[180px] bg-slate-50 border-l border-slate-200 text-slate-500 font-bold" }, // 10
  ];

  const isAdmin = auth?.role === 'ROLE_ADMIN';

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">

      <PageHeader
        icon={Users}
        iconBgColor="bg-blue-600"
        title="사업장 배정 관리"
        description="사업장에 방문할 수행요원을 배정하고 관리합니다."
        auth={auth}
        helpId="company_assignment"
        initialHelpText={initialHelpText}
      />

      <FilterBar
        onReset={handleReset}
        isSearching={query.isFetching}
        actions={
          <>
            {/* 🌟 (수정) 관리자일 때만 등록, 삭제, 엑셀 업로드 버튼 표시 */}
            {isAdmin && (
              <>
                <button onClick={handleRegistClick} className="flex h-10 items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 text-sm font-medium text-blue-700 hover:bg-blue-100 transition-colors">
                  <Plus className="h-4 w-4" /> 등록
                </button>
              </>
            )}

            <button
              onClick={handleDeleteClick}
              disabled={!anyChecked || isDeleting}
              className="flex h-10 items-center gap-2 rounded-lg border border-red-200 bg-white px-4 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Trash2 className="h-4 w-4" /> 삭제 {anyChecked && `(${selectedIds.length})`}
            </button>

            {/* 엑셀 다운로드는 일반 사용자도 가능하게 둠 (원하지 않으면 이것도 isAdmin으로 묶으세요) */}
            <button onClick={downloadExcel} disabled={isDownloadingExcel} className="flex h-10 items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50">
              {isDownloadingExcel ? <Loader2 className="h-4 w-4 text-green-600 animate-spin" /> : <Download className="h-4 w-4 text-green-600" />}
              엑셀 다운로드
            </button>

            {isAdmin && (
              <button onClick={() => setIsExcelOpen(true)} className="flex h-10 items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
                <FileSpreadsheet className="h-4 w-4 text-green-600" /> 엑셀 업로드
              </button>
            )}
          </>
        }
      >
        {/* <div className="space-y-1.5 flex-1 min-w-[120px] max-w-[160px]">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">등록 지사</label>
          <div className="relative">
            <select className="h-10 w-full appearance-none rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer" value={filters.branch} onChange={(e) => setFilters(p => ({ ...p, branch: e.target.value }))}>
              <option value="">전체 지사</option>
              <option value="서울광역본부">서울광역본부</option>
              <option value="강원지역본부">강원지역본부</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
        </div> */}

        {/* 🌟 추가됨: 회사명 검색 */}
        <div className="space-y-1.5 flex-1 min-w-[150px] max-w-[200px]">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">회사명</label>
          <input type="text" placeholder="회사명 검색" className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" value={filters.companyName} onChange={(e) => setFilters(p => ({ ...p, companyName: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && onSearch()} />
        </div>

        {/* 🌟 추가됨: 방문 회차 검색 */}
        {/* <div className="space-y-1.5 flex-1 min-w-[100px] max-w-[140px]">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">방문 회차</label>
          <div className="relative">
            <select className="h-10 w-full appearance-none rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer" value={filters.visitRound} onChange={(e) => setFilters(p => ({ ...p, visitRound: e.target.value }))}>
              <option value="">전체 회차</option>
              <option value="1">1회차</option>
              <option value="2">2회차</option>
              <option value="3">3회차</option>
              <option value="4">4회차</option>
              <option value="5">5회차</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
        </div> */}

        {isAdmin && (
          <div className="space-y-1.5 flex-1 min-w-[150px] max-w-[200px]">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">방문 담당자명</label>
            <input type="text" placeholder="담당자명 검색" className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" value={filters.assignee} onChange={(e) => setFilters(p => ({ ...p, assignee: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && onSearch()} />
          </div>
        )}
      </FilterBar>

      {/* 🌟 (수정) max-h 고정, 테이블 헤더(thead) sticky 고정 속성 완벽 추가 */}
      <div className={clsx(
        "[&>div]:max-h-[600px] [&>div]:flex [&>div]:flex-col",
        "[&>div>div.overflow-x-auto]:flex-1 [&>div>div.overflow-x-auto]:overflow-y-auto [&>div>div.overflow-x-auto]:custom-scrollbar",
        "[&>div>div.overflow-x-auto>table>thead]:sticky [&>div>div.overflow-x-auto>table>thead]:top-0 [&>div>div.overflow-x-auto>table>thead]:z-30",
        "[&>div>div.overflow-x-auto>table>thead>tr>th]:bg-slate-50 [&>div>div.overflow-x-auto>table>thead>tr>th]:shadow-[inset_0_-1px_0_0_#e2e8f0]"
      )}>
        <DataTable
          columns={tableColumns}
          page={page}
          itemName="배정 그룹"
          onPageChange={(newPage) => setFilters(p => ({ ...p, pageNum: newPage }))}
          emptyMessage="등록된 사업장이 없습니다."
          renderRow={(group) => {
            const groupId = group.bizMgtNo;
            const editData = editingRows[groupId] || {};
            const isExpanded = expandedRows[groupId];
            const displayStaff1 = group.staffNm1 || group.staffId1 || '미배정';

            return (
              <React.Fragment key={groupId}>
                {/* --- 1. 회사(그룹) 단위 부모 행 --- */}
                <tr
                  onClick={() => toggleExpand(groupId)}
                  className="group/row bg-white font-medium hover:bg-blue-50/50 transition-colors cursor-pointer border-b border-slate-200"
                >
                  <td className="px-4 py-4 text-center text-slate-400 group-hover/row:text-blue-600 transition-colors">
                    {isExpanded ? <ChevronDown className="h-5 w-5 mx-auto" /> : <ChevronRight className="h-5 w-5 mx-auto" />}
                  </td>
                  <td className="px-6 py-4 font-bold text-slate-900">{group.companyName || '-'}</td>
                  <td className="px-6 py-4 text-slate-700">{group.ceoName || '-'}</td>
                  <td className="px-6 py-4 text-slate-700 text-right">{group.employeeCount?.toLocaleString() || '-'}</td>
                  <td className="px-6 py-4 text-slate-700">{group.businessType || '-'}</td>
                  <td className="px-6 py-4 text-slate-500 truncate max-w-[200px]" title={group.address}>{group.address || '-'}</td>
                  <td className="px-6 py-4 text-slate-700">{group.managerName || '-'}</td>
                  <td className="px-6 py-4 text-slate-700">{group.managerTel || '-'}</td>

                  {/* 🌟 담당자 1 (통합) 인라인 편집 - 관리자 분기 처리 */}
                  <td className="px-6 py-3 bg-blue-50/40 border-l border-blue-100/50" onClick={(e) => e.stopPropagation()}>
                    {editData.editType === 'staff1' ? (
                      <div className="flex items-center gap-1 animate-in fade-in zoom-in duration-200">
                        <select className="h-9 flex-1 rounded border border-blue-300 bg-white px-2 text-sm focus:ring-2 focus:ring-blue-200" value={editData.staffId1 || ''} onChange={(e) => updateEditRow(groupId, { staffId1: e.target.value })}>
                          <option value="">선택</option>
                          {staffList.map((m) => <option key={m.staffId} value={m.staffId}>{m.staffNm}</option>)}
                        </select>
                        <button onClick={() => saveRow(group, true)} className="p-1 rounded bg-green-100 text-green-700 hover:bg-green-200"><Check className="h-4 w-4" /></button>
                        <button onClick={() => cancelEdit(groupId)} className="p-1 rounded bg-red-100 text-red-700 hover:bg-red-200"><X className="h-4 w-4" /></button>
                      </div>
                    ) : (
                      // 관리자면 셀렉트 박스 스타일의 편집 버튼, 아니면 일반 텍스트
                      isAdmin ? (
                        <div onClick={(e) => { e.stopPropagation(); startEdit(group, 'staff1', true); }} className="flex items-center justify-between gap-2 rounded-md border border-blue-200 bg-white px-3 py-2 text-sm text-blue-800 shadow-sm hover:border-blue-400 hover:ring-1 hover:ring-blue-400 transition-all cursor-pointer">
                          <span className="font-bold">{displayStaff1}</span>
                          <ChevronDown className="h-3.5 w-3.5 text-blue-400" />
                        </div>
                      ) : (
                        <div className="px-3 py-2 text-sm font-bold text-slate-700 text-center">
                          {displayStaff1}
                        </div>
                      )
                    )}
                  </td>

                  {/* 담당자 2 (그룹행은 비워둠) */}
                  <td className="px-6 py-3 bg-slate-50/50 border-l border-slate-100 text-center text-slate-300">-</td>
                </tr>

                {/* --- 2. 개별 회차(Sub-rows) 독립된 서브 테이블로 렌더링 --- */}
                {isExpanded && (
                  <tr className="bg-slate-50/60 border-b-2 border-slate-200 shadow-[inset_0px_5px_8px_-6px_rgba(0,0,0,0.1)]">
                    <td colSpan={10} className="p-0">
                      <div className="py-4 pl-[4.5rem] pr-6">
                        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden shadow-sm">
                          <table className="w-full text-sm">
                            <thead className="bg-slate-100/80 border-b border-slate-200 text-slate-500 text-xs font-semibold uppercase tracking-wide">
                              <tr>
                                <th className="px-4 py-3 text-center w-14">선택</th>
                                <th className="px-4 py-3 text-center w-20">회차</th>
                                <th className="px-6 py-3 text-left">방문 상세내역 (방문 예정일 / 특이사항)</th>
                                <th className="px-6 py-3 text-left w-[260px] bg-indigo-50/50 border-l border-indigo-100 text-indigo-700">방문 담당자 2 (회차별 설정)</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {group.rounds?.map((round) => {
                                const roundId = round.assignNo;
                                const rEditData = editingRows[roundId] || {};
                                const displayStaff2 = round.staffNm2 || round.staffId2 || '미배정';
                                const isChecked = selectedIds.includes(roundId);

                                return (
                                  <tr key={roundId} className={clsx("transition-colors", isChecked ? 'bg-blue-50/40' : 'hover:bg-slate-50')}>
                                    <td className="px-4 py-3 text-center">
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={(e) => toggleSelectRow(roundId, e.target.checked)}
                                        className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                      />
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                      <span className="font-bold text-indigo-600 bg-indigo-50 px-2.5 py-0.5 rounded-full text-xs border border-indigo-100 shadow-sm">{round.visitRound}회차</span>
                                    </td>
                                    <td className="px-6 py-3 text-slate-600">
                                      <span className="mr-3 font-semibold text-slate-700">방문 예정일:</span>
                                      {round.visitDate ? `${round.visitDate}` : '미정'}
                                      {round.remarks && <span className="ml-3 text-xs text-slate-500 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded">({round.remarks})</span>}
                                    </td>
                                    <td className="px-6 py-2.5 bg-indigo-50/20 border-l border-indigo-100/50">
                                      {rEditData.editType === 'staff2' ? (
                                        <div className="flex items-center gap-1 animate-in fade-in zoom-in duration-200">
                                          <select className="h-8 flex-1 rounded border border-indigo-300 bg-white px-2 text-xs focus:ring-2 focus:ring-indigo-200" value={rEditData.staffId2 || ''} onChange={(e) => updateEditRow(roundId, { staffId2: e.target.value })}>
                                            <option value="">선택 안함</option>
                                            {staffList.filter(m => m.staffId !== group.staffId1).map((m) => <option key={m.staffId} value={m.staffId}>{m.staffNm}</option>)}
                                          </select>
                                          <button onClick={() => saveRow(round, false)} className="p-1 rounded bg-green-100 text-green-700 hover:bg-green-200 shadow-sm"><Check className="h-3 w-3" /></button>
                                          <button onClick={() => cancelEdit(roundId)} className="p-1 rounded bg-red-100 text-red-700 hover:bg-red-200 shadow-sm"><X className="h-3 w-3" /></button>
                                        </div>
                                      ) : (
                                        <div onClick={(e) => { e.stopPropagation(); startEdit(round, 'staff2', false); }} className="flex items-center justify-between gap-2 rounded border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-700 shadow-sm cursor-pointer hover:border-indigo-400 transition-all">
                                          <span className="font-medium">{displayStaff2}</span>
                                          <ChevronDown className="h-3 w-3 text-slate-400" />
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}

                              {(!group.rounds || Math.max(...group.rounds.map(r => r.visitRound), 0) < 5) && (
                                <tr>
                                  <td colSpan={4} className="px-4 py-2.5 text-center bg-blue-50/20 border-t border-slate-200 hover:bg-blue-50/50 transition-colors cursor-pointer" onClick={(e) => { e.stopPropagation(); handleAddRound(group); }}>
                                    <button
                                      className="inline-flex items-center justify-center gap-1.5 text-xs font-bold text-blue-600 w-full"
                                      disabled={isRegisting}
                                    >
                                      {isRegisting ? <Loader2 className="h-4 w-4 animate-spin text-blue-400" /> : <Plus className="h-4 w-4" />}
                                      {isRegisting ? '회차 추가 중...' : '다음 회차 배정 추가하기'}
                                    </button>
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          }}
        />
      </div>

      <ExcelUploadModal
        isOpen={isExcelOpen}
        onClose={() => setIsExcelOpen(false)}
        onUpload={handleExcelUpload}
        isUploading={isUploading}
        title="배정 엑셀 일괄 업로드"
      />

      <AssignmentRegistModal
        isOpen={isRegistModalOpen}
        onClose={() => setIsRegistModalOpen(false)}
        onSave={registAssignment}
        isSaving={isRegisting}
        staffList={staffList}
        bizList={bizList}
      />
    </div>
  );
}